import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { resetPasswordSendEmail } from "@/services/authService";
import AuthLayout from "@/components/auth/AuthLayout";
import AuthField from "@/components/auth/AuthField";
import AuthAlert from "@/components/auth/AuthAlert";
import { getEmailError, SUPPORT_EMAIL } from "@/components/auth/authCopy";

/** Seconds before "Send it again" re-arms, per the prototype. */
const RESEND_COOLDOWN = 30;

export default function ForgotPasswordEmail() {
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(
        () => () => {
            if (timerRef.current) clearInterval(timerRef.current);
        },
        [],
    );

    const startCooldown = () => {
        setCooldown(RESEND_COOLDOWN);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setCooldown((left) => {
                if (left <= 1) {
                    if (timerRef.current) clearInterval(timerRef.current);
                    timerRef.current = null;
                    return 0;
                }
                return left - 1;
            });
        }, 1000);
    };

    const checkEmail = () => {
        const message = getEmailError(email);
        setEmailError(message);
        return message === null;
    };

    const send = async () => {
        setError(null);
        setLoading(true);
        try {
            const response = await resetPasswordSendEmail(email.trim());
            if (response.status === 204) {
                setSent(true);
                startCooldown();
            } else {
                setError("We couldn't send that email. Try again in a moment.");
            }
        } catch {
            setError("We couldn't send that email. Try again in a moment.");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!checkEmail()) return;
        await send();
    };

    // ── Check your email ─────────────────────────────────────────────────
    if (sent) {
        return (
            <AuthLayout
                page="check"
                variant="narrow"
                icon={<Mail size={26} strokeWidth={2} />}
                title="Check your email"
                subtitle={
                    <>
                        We sent a link to <strong>{email.trim()}</strong>. Open it on this{" "}
                        <span className="d-only">device</span>
                        <span className="m-only">phone</span> and you'll be able to set a new
                        password.
                    </>
                }
            >
                <div className="auth-panel">
                    <span className="q">Don't see it after a minute?</span>
                    <span className="a">
                        Look in your spam or junk folder. The sender is AITrustSignals.
                    </span>
                </div>

                {error ? <AuthAlert>{error}</AuthAlert> : null}

                <button
                    className="auth-btn-primary"
                    type="button"
                    onClick={send}
                    disabled={loading || cooldown > 0}
                >
                    {cooldown > 0
                        ? `Sent. You can send it again in ${cooldown}s`
                        : loading
                          ? "Sending…"
                          : "Send it again"}
                </button>

                <Link className="auth-link auth-link--lg auth-back" to="/login">
                    Back to sign in
                </Link>

                <div className="auth-help auth-help--single d-only">
                    Still stuck? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </div>
            </AuthLayout>
        );
    }

    // ── Forgot your password ─────────────────────────────────────────────
    return (
        <AuthLayout
            page="forgot"
            variant="narrow"
            title="Forgot your password?"
            subtitle="It happens. Type the email you signed up with and we'll send you a link to make a new password."
        >
            <form onSubmit={handleForgotPassword} noValidate>
                <AuthField
                    fieldClassName="auth-field--email"
                    label="Email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="you@yourcompany.com"
                    mobilePlaceholder="Email"
                    value={email}
                    error={emailError}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError(null);
                    }}
                    onBlur={() => {
                        if (email.trim()) checkEmail();
                    }}
                />

                {error ? <AuthAlert>{error}</AuthAlert> : null}

                <button className="auth-btn-primary" type="submit" disabled={loading}>
                    {loading ? "Sending…" : "Send me the link"}
                </button>
            </form>

            <Link className="auth-link auth-link--lg auth-back" to="/login">
                Back to sign in
            </Link>

            <div className="auth-help">
                <span className="q">Not sure which email you used?</span>
                <span className="a">
                    Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll look
                    it up for you.
                </span>
            </div>
        </AuthLayout>
    );
}
